import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { getApiBase, getAuthHeaders, getActingOrganizationId, setActingOrganizationId, clearActingOrganizationId } from '../utils/api';
import { UNAUTHORIZED_EVENT } from '../utils/browserSecurity';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [autoLoginAttempted, setAutoLoginAttempted] = useState(false);
  const [debugMessage, setDebugMessage] = useState('Initializing...');

  // Superadmin: acting organization state
  const [actingOrganization, setActingOrganizationState] = useState(null);
  const [organizations, setOrganizations] = useState([]);

  const establishBrowserSession = useCallback(async () => {
    const apiBase = getApiBase();
    const { data } = await axios.get(`${apiBase}/auth/me`, { withCredentials: true });
    if (!data?.user) throw new Error('Your browser session could not be established.');
    const userPayload = data.user;
    try {
      userPayload.ui_preferences = (await axios.get(`${apiBase}/users/me/preferences`)).data;
    } catch (error) {
      console.warn('Could not fetch user preferences');
    }
    localStorage.removeItem('token');
    sessionStorage.setItem('user', JSON.stringify(userPayload));
    setUser(userPayload);
    return { success: true };
  }, []);

  const login = useCallback(async (email, password) => {
    try {
      await axios.post(`${getApiBase()}/auth/login`, { email, password });
      return await establishBrowserSession();
    } catch (error) {
      setUser(null);
      sessionStorage.removeItem('user');
      return { success: false, error: error.response?.data?.detail || error.message || 'Login failed' };
    }
  }, [establishBrowserSession]);

  const register = async (userData) => {
    try {
      await axios.post(`${getApiBase()}/auth/register`, userData);
      return await establishBrowserSession();
    } catch (error) {
      setUser(null);
      sessionStorage.removeItem('user');
      return { success: false, error: error.response?.data?.detail || error.message || 'Registration failed' };
    }
  };

  const loginWithPasskey = useCallback(async () => {
    try {
      return await establishBrowserSession();
    } catch (error) {
      setUser(null);
      sessionStorage.removeItem('user');
      return { success: false, error: error.message || 'Passkey login failed' };
    }
  }, [establishBrowserSession]);

  // Sign in with an emailed one-time code (OTP). The backend sets the session
  // cookie on verify; we then reuse the same /auth/me flow as passkey login.
  const loginWithEmailCode = useCallback(async (email, code) => {
    try {
      const apiBase = getApiBase();
      const verifyResponse = await axios.post(
        `${apiBase}/auth/otp/verify`,
        { email, code },
        { withCredentials: true }
      );
      return await loginWithPasskey(verifyResponse.data);
    } catch (error) {
      console.error('Email code login error:', error);
      return {
        success: false,
        error: error.response?.data?.detail || 'Invalid or expired code'
      };
    }
  }, [loginWithPasskey]);

  // Fetch organizations the user can access
  const fetchOrganizations = useCallback(async () => {
    if (!user) return [];
    try {
      const apiBase = getApiBase();
      let orgs = [];
      if (user.role === 'superadmin') {
        // Superadmins see all organizations
        const response = await axios.get(`${apiBase}/admin/organizations`, {
          headers: getAuthHeaders(),
          withCredentials: true
        });
        orgs = Array.isArray(response.data) ? response.data : [];
      } else {
        // Non-superadmins see their primary org + additional memberships
        const response = await axios.get(`${apiBase}/me/accessible-organizations`, {
          headers: getAuthHeaders(),
          withCredentials: true
        });
        orgs = Array.isArray(response.data) ? response.data : [];
      }
      setOrganizations(orgs);
      return orgs;
    } catch (error) {
      console.error('Failed to fetch organizations:', error);
      return [];
    }
  }, [user]);

  // Switch to a different organization
  const switchOrganization = useCallback(async (org) => {
    if (!user) return { success: false };

    if (!org) {
      // Clear acting org - return to own organization
      clearActingOrganizationId();
      setActingOrganizationState(null);
      return { success: true };
    }

    // Superadmins switch immediately (no validation needed)
    if (user.role === 'superadmin') {
      setActingOrganizationId(org.id);
      setActingOrganizationState(org);
      return { success: true };
    }

    // Non-superadmins: validate switch via backend (checks membership + auth method)
    try {
      const apiBase = getApiBase();
      const response = await axios.post(`${apiBase}/auth/switch-organization`, {
        organization_id: org.id
      }, {
        headers: getAuthHeaders(),
        withCredentials: true
      });

      if (response.data.success) {
        setActingOrganizationId(org.id);
        setActingOrganizationState(org);
        return { success: true };
      }

      if (response.data.requires_reauthentication) {
        // Return re-auth info so the caller can handle it
        return {
          success: false,
          requires_reauthentication: true,
          auth_type: response.data.auth_type,
          idp_name: response.data.idp_name,
          organization_name: response.data.organization_name,
          message: response.data.message
        };
      }

      return { success: false, error: 'Switch failed' };
    } catch (error) {
      console.error('Failed to switch organization:', error);
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to switch organization'
      };
    }
  }, [user]);

  // Set a different organization as primary
  const setPrimaryOrganization = useCallback(async (orgId) => {
    if (!user) return { success: false };
    try {
      const apiBase = getApiBase();
      const response = await axios.post(`${apiBase}/auth/set-primary-organization`, {
        organization_id: orgId
      }, {
        headers: getAuthHeaders(),
        withCredentials: true
      });

      if (response.data.success) {
        // Clear acting org since we're now "home" in the new primary
        clearActingOrganizationId();
        setActingOrganizationState(null);


        return { success: true };
      }
      return { success: false, error: 'Failed to set primary organization' };
    } catch (error) {
      console.error('Failed to set primary organization:', error);
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to set primary organization'
      };
    }
  }, [user]);

  // Initialize acting organization from localStorage on mount
  useEffect(() => {
    if (user) {
      const storedOrgId = getActingOrganizationId();
      if (storedOrgId && !actingOrganization) {
        // Fetch organizations to find the stored one
        fetchOrganizations().then(orgs => {
          const org = orgs.find(o => o.id === storedOrgId);
          if (org) {
            setActingOrganizationState(org);
          } else {
            // Stored org no longer exists or user lost access, clear it
            clearActingOrganizationId();
          }
        });
      }
    }
  }, [user, actingOrganization, fetchOrganizations]);

  const logout = useCallback(async () => {
    try {
      // Call logout endpoint to clear session cookie
      const apiBase = getApiBase();
      await axios.post(`${apiBase}/auth/logout`, {}, {
        withCredentials: true  // Ensure cookies are sent/cleared
      });
    } catch (error) {
      // Logout endpoint may fail if session already expired
    }

    // SECURITY: Clear ALL local storage and cookies (preserve saved login email)
    const savedLoginEmail = localStorage.getItem('veyage_saved_login_email');
    localStorage.clear();  // Clear everything, not just token and user
    if (savedLoginEmail) {
      localStorage.setItem('veyage_saved_login_email', savedLoginEmail);
    }
    sessionStorage.clear();  // Also clear session storage

    // Clear axios headers
    delete axios.defaults.headers.common['Authorization'];

    // Clear any lingering cookies (session_token) manually
    document.cookie.split(";").forEach(function(c) {
      document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
    });

    // Clear user state and acting organization
    setUser(null);
    setActingOrganizationState(null);
    setOrganizations([]);
  }, []);

  // When the server-side session times out, API calls start returning 401 while
  // the app still holds the old user. Confirm with /auth/me (some endpoints 401
  // for other reasons) and, if the session is really gone, sign out locally so
  // the login screen appears without a page refresh.
  useEffect(() => {
    if (!user) return;
    let checking = false;
    const handleUnauthorized = async () => {
      if (checking) return;
      checking = true;
      try {
        await axios.get(`${getApiBase()}/auth/me`, { withCredentials: true });
      } catch (error) {
        if (error.response?.status === 401) {
          const returnUrl = window.location.pathname + window.location.search;
          await logout();
          if (returnUrl !== '/' && !returnUrl.startsWith('/login')) {
            sessionStorage.setItem('authReturnUrl', returnUrl);
          }
        }
      } finally {
        checking = false;
      }
    };
    window.addEventListener(UNAUTHORIZED_EVENT, handleUnauthorized);
    return () => window.removeEventListener(UNAUTHORIZED_EVENT, handleUnauthorized);
  }, [user, logout]);

  useEffect(() => {
    let isMounted = true;

    const initializeAuth = async () => {
      // Authenticate only through the HttpOnly session cookie.
      try {
        const response = await axios.get(`${getApiBase()}/auth/me`, {
          headers: getAuthHeaders(),
          withCredentials: true
        });
        if (response.data && response.data.user && isMounted) {
          // Merge ui preferences if present
          const userPayload = response.data.user;
          try {
            const prefs = (await axios.get(`${getApiBase()}/users/me/preferences`, {
              headers: getAuthHeaders(),
              withCredentials: true
            })).data;
            userPayload.ui_preferences = prefs;
          } catch {}
          setUser(userPayload);
          setLoading(false);
          return;
        }
      } catch (error) {
        // Session cookie auth failed
        console.error('Auth check failed:', error?.response?.status, error?.response?.data?.detail || error.message);

        // If there's an acting org ID and auth failed, it might be causing the issue
        // Clear it and retry once
        const actingOrgId = localStorage.getItem('actingOrganizationId');
        if (actingOrgId) {
          console.warn('Clearing acting organization ID and retrying auth...');
          localStorage.removeItem('actingOrganizationId');
          try {
            const retryResponse = await axios.get(`${getApiBase()}/auth/me`, {
              headers: getAuthHeaders(),
              withCredentials: true
            });
            if (retryResponse.data && retryResponse.data.user && isMounted) {
              const userPayload = retryResponse.data.user;
              try {
                const prefs = (await axios.get(`${getApiBase()}/users/me/preferences`, {
                  headers: getAuthHeaders(),
                  withCredentials: true
                })).data;
                userPayload.ui_preferences = prefs;
              } catch {}
              setUser(userPayload);
              setLoading(false);
              return;
            }
          } catch (retryError) {
            console.error('Auth retry also failed:', retryError?.response?.status);
          }
        }
      }

      // SECURITY: Removed JWT token authentication
      // Now relying exclusively on session cookies for authentication
      // Clean up any legacy tokens from localStorage (use sessionStorage now)
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      // Note: sessionStorage tokens are valid for this tab only

      // SECURITY FIX: Disabled auto-login by default as it causes identity confusion
      // Only enable in development if explicitly set via environment variable
      const enableAutoLogin = process.env.REACT_APP_ENABLE_AUTO_LOGIN === 'true';

      if (enableAutoLogin && !autoLoginAttempted && isMounted) {
        setAutoLoginAttempted(true);
        setDebugMessage('Starting auto-login...');

        // Add delay for preview environment stability
        setTimeout(() => {
          if (!isMounted) return;

          setDebugMessage('🔄 Calling login API...');

          // Use environment variables for auto-login credentials
          const autoLoginEmail = process.env.REACT_APP_DEV_EMAIL;
          const autoLoginPassword = process.env.REACT_APP_DEV_PASSWORD;

          if (!autoLoginEmail || !autoLoginPassword) {
            console.warn('Auto-login disabled: credentials not configured');
            setDebugMessage('Auto-login disabled: credentials not configured');
            setLoading(false);
            return;
          }

          // Use the same login function that the Auto Login button uses
          login(autoLoginEmail, autoLoginPassword)
            .then(result => {
              if (!isMounted) return;

              if (result && result.success) {
                setDebugMessage('Auto-login successful!');
              } else {
                const errorMsg = result ? result.error : 'No result returned';
                setDebugMessage(`Failed: ${errorMsg}`);
              }
              setLoading(false);
            })
            .catch(error => {
              if (!isMounted) return;
              setDebugMessage(`Error: ${error.message || 'Unknown error'}`);
              setLoading(false);
            });
        }, 1000); // 1 second delay for preview environment
      } else {
        // No auto-login, ready for manual login
        if (isMounted) {
          setDebugMessage('Ready - please log in');
          setLoading(false);
        }
      }
    };

    initializeAuth();
    
    // Cleanup function
    return () => {
      isMounted = false;
    };
  }, []); // Empty dependency array to run only once

  // Handle loading state when auto-login is not needed
  useEffect(() => {
    if (autoLoginAttempted && !loading) {
      // Auto-login has been attempted and completed
      return;
    }
    // SECURITY: Removed token check, relying on session cookies
  }, [autoLoginAttempted, loading]);

  const hasMultipleOrgs = organizations.length > 1;

  const value = {
    user,
    setUser, // Add setUser to the context
    loading,
    login,
    loginWithPasskey,
    loginWithEmailCode,
    register,
    logout,
    debugMessage, // Add debug message for troubleshooting
    // Organization switching (superadmins + multi-org users)
    actingOrganization,
    organizations,
    hasMultipleOrgs,
    fetchOrganizations,
    switchOrganization,
    setPrimaryOrganization
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};